import { useState } from "react";
import { motion } from "framer-motion";
import { User, Users, Building, ArrowRight, ArrowLeft, TreePine, Zap, Truck, Lightbulb } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SIZES = [
  { id: "small", label: "Small", sub: "1 - 10 employees", icon: User },
  { id: "medium", label: "Medium", sub: "10 - 100 employees", icon: Users },
  { id: "large", label: "Large", sub: "100+ employees", icon: Building },
];

const IMPACT_ICONS = { energy: Zap, materials: TreePine, transport: Truck, waste: TreePine };

export function Step3ScalePersonalization({ companyId, onComplete, onBack }) {
  const [selectedSize, setSelectedSize] = useState(null);
  const [loading, setLoading] = useState(false);
  const [insight, setInsight] = useState(null);

  const handleSubmit = async (size) => {
    setSelectedSize(size);
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/personalization/generate`, {
        company_id: companyId,
        size,
      });
      setInsight(data);
    } catch (err) {
      toast.error("Could not generate insight.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <p className="text-sm text-muted-foreground uppercase tracking-widest mb-2">Your scale</p>
        <h2 className="text-3xl md:text-5xl font-semibold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
          How large is your company?
        </h2>
        <p className="text-base text-muted-foreground mt-3 leading-relaxed">
          This helps us calibrate your sustainability profile.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {SIZES.map((size) => {
          const Icon = size.icon;
          const isActive = selectedSize === size.id;
          return (
            <motion.div
              key={size.id}
              whileHover={{ y: -4 }}
              onClick={() => !insight && handleSubmit(size.id)}
              className={`glass-card p-6 cursor-pointer text-center transition-all duration-300 ${isActive ? 'glass-card-selected' : ''}`}
              data-testid={`size-${size.id}`}
            >
              <div className={`w-14 h-14 rounded-2xl mx-auto mb-4 flex items-center justify-center ${isActive ? 'bg-primary/20' : 'bg-white/5'} transition-colors`}>
                <Icon className={`w-7 h-7 ${isActive ? 'text-primary' : 'text-white/50'}`} />
              </div>
              <h3 className="text-xl font-semibold text-white mb-1">{size.label}</h3>
              <p className="text-sm text-white/40">{size.sub}</p>
            </motion.div>
          );
        })}
      </div>

      {loading && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass-card p-8 text-center"
        >
          <div className="w-8 h-8 border-2 border-primary/30 border-t-primary rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Generating your personalized insight...</p>
        </motion.div>
      )}

      {insight && !loading && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-4"
          data-testid="personalization-insight"
        >
          {insight.comparison && (
            <motion.div
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.15 }}
              className="rounded-2xl p-5 border border-primary/25 flex items-start gap-4"
              style={{ background: "linear-gradient(135deg, rgba(74,222,128,0.08) 0%, rgba(2,4,2,0.95) 100%)" }}
              data-testid="did-you-know-card"
            >
              <div className="w-10 h-10 rounded-xl bg-primary/15 flex items-center justify-center flex-shrink-0">
                <Lightbulb className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-xs text-primary font-semibold uppercase tracking-widest mb-1">Did you know?</p>
                <p className="text-base text-white leading-relaxed font-medium">{insight.comparison}</p>
              </div>
            </motion.div>
          )}

          <div className="glass-card glass-card-selected p-8 space-y-5">
            <div className="flex items-center gap-3 mb-1">
              {(() => {
                const ImpactIcon = IMPACT_ICONS[insight.impact_area] || TreePine;
                return <ImpactIcon className="w-6 h-6 text-primary" />;
              })()}
              <span className="text-sm font-medium text-primary uppercase tracking-wider">{insight.impact_area}</span>
            </div>
            <p className="text-xl text-white leading-relaxed">{insight.insight}</p>

            {insight.reduction_potential && (
              <div className="flex items-center gap-2">
                <ArrowRight className="w-4 h-4 text-primary flex-shrink-0" />
                <p className="text-sm text-primary/80">{insight.reduction_potential}</p>
              </div>
            )}
          </div>
        </motion.div>
      )}

      {!insight && !loading && (
        <div className="flex items-center gap-3">
          {onBack && (
            <Button
              data-testid="scale-back-btn"
              onClick={onBack}
              variant="ghost"
              className="rounded-full px-5 h-14 text-muted-foreground hover:text-white text-lg"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back
            </Button>
          )}
        </div>
      )}

      {insight && (
        <div className="flex items-center gap-3">
          {onBack && (
            <Button
              data-testid="scale-back-btn"
              onClick={onBack}
              variant="ghost"
              className="rounded-full px-5 h-14 text-muted-foreground hover:text-white text-lg"
            >
              <ArrowLeft className="w-5 h-5 mr-2" />
              Back
            </Button>
          )}
          <Button
            data-testid="scale-continue-btn"
            onClick={onComplete}
            className="rounded-full px-8 h-14 bg-primary text-primary-foreground text-lg font-medium btn-glow"
          >
            Continue
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      )}
    </div>
  );
}

export default Step3ScalePersonalization;
